import { useState } from 'react';
import { Card, Button, Badge, getStatusBadge, Avatar } from '@/backoffice/components/ui';
import { OrderChatPanel } from '@/backoffice/components/messaging/OrderChatPanel';
import { formatCurrency, formatRelativeTime } from '@/backoffice/utils';
import type { DriverMission } from '@/domains/driver/services/driver.service';
import {
  Package,
  MapPin,
  Check,
  X,
  Phone,
  MessageSquare,
} from 'lucide-react';

interface DriverMissionCardProps {
  mission: DriverMission;
  onAccept?: () => void;
  onReject?: () => void;
  onComplete?: () => void;
  compact?: boolean;
  actionLoading?: boolean;
}

export function DriverMissionCard({
  mission,
  onAccept,
  onReject,
  onComplete,
  compact = false,
  actionLoading = false,
}: DriverMissionCardProps) {
  const [showChat, setShowChat] = useState(false);
  const canAcceptReject = mission.status === 'pending' || mission.isAvailable;
  const isInProgress = mission.status === 'in_progress';
  const canChat = !mission.isAvailable && mission.status !== 'rejected';
  const clientName = mission.client
    ? `${mission.client.firstnames} ${mission.client.lastname}`
    : 'Client';

  if (compact) {
    return (
      <div className="h-full flex flex-col p-3.5 bg-white rounded-xl border border-slate-200 hover:border-blue-300 hover:shadow-md transition-all shadow-sm">
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="min-w-0">
            <p className="font-semibold text-sm text-slate-900 truncate">{mission.code}</p>
            <p className="text-[11px] text-slate-400 mt-0.5">{formatRelativeTime(mission.createdAt)}</p>
          </div>
          {mission.isAvailable ? (
            <Badge variant="secondary" size="sm">Dispo</Badge>
          ) : (
            <div className="scale-90 origin-top-right">{getStatusBadge(mission.status)}</div>
          )}
        </div>

        <div className="flex items-center gap-1.5 text-xs text-slate-600 mb-2.5">
          <MapPin className="w-3 h-3 text-slate-400 shrink-0" />
          <span className="truncate">{mission.pickup.zone}</span>
          <span className="text-slate-300">→</span>
          <MapPin className="w-3 h-3 text-emerald-500 shrink-0" />
          <span className="truncate">{mission.dropoff.zone}</span>
        </div>

        <div className="mt-auto pt-2.5 border-t border-slate-100 flex items-center justify-between gap-2">
          <p className="text-sm font-bold text-emerald-600 tabular-nums">{formatCurrency(mission.price)}</p>
          {!mission.isAvailable && mission.client && (
            <span className="text-[11px] text-slate-400 truncate">{clientName}</span>
          )}
        </div>

        {canAcceptReject && onAccept && onReject && (
          <div className="flex gap-1.5 mt-2.5">
            <Button
              variant="outline"
              size="sm"
              className="flex-1 !px-2"
              leftIcon={<X className="w-3.5 h-3.5" />}
              onClick={onReject}
              disabled={actionLoading}
            >
              Non
            </Button>
            <Button
              variant="success"
              size="sm"
              className="flex-1 !px-2"
              leftIcon={<Check className="w-3.5 h-3.5" />}
              onClick={onAccept}
              isLoading={actionLoading}
            >
              Prendre
            </Button>
          </div>
        )}
        {isInProgress && onComplete && (
          <Button
            variant="success"
            size="sm"
            className="w-full mt-2.5"
            leftIcon={<Check className="w-3.5 h-3.5" />}
            onClick={onComplete}
            isLoading={actionLoading}
          >
            Livrée
          </Button>
        )}
      </div>
    );
  }

  return (
    <Card>
      <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                mission.isAvailable ? 'bg-violet-100 text-violet-600' :
                mission.status === 'delivered' ? 'bg-emerald-100 text-emerald-600' :
            mission.status === 'in_progress' ? 'bg-blue-100 text-blue-600' :
            mission.status === 'rejected' ? 'bg-red-100 text-red-600' :
            'bg-amber-100 text-amber-600'
          }`}>
            <Package className="w-6 h-6" />
          </div>
          <div>
            <p className="font-semibold text-gray-900">{mission.code}</p>
            <p className="text-sm text-gray-500">{formatRelativeTime(mission.createdAt)}</p>
          </div>
        </div>
        {mission.isAvailable ? (
          <Badge variant="secondary">Disponible</Badge>
        ) : (
          getStatusBadge(mission.status)
        )}
      </div>

      <div className="flex gap-4 mb-4">
        <div className="flex flex-col items-center gap-1">
          <div className="w-3 h-3 rounded-full bg-emerald-500 border-2 border-emerald-200" />
          <div className="w-0.5 flex-1 bg-gray-300" />
          <div className="w-3 h-3 rounded-full bg-red-500 border-2 border-red-200" />
        </div>
        <div className="flex-1 space-y-3">
          <div className="bg-emerald-50 rounded-lg p-3">
            <p className="text-xs text-emerald-700 font-medium">POINT DE COLLECTE</p>
            <p className="font-medium text-gray-900">{mission.pickup.zone}</p>
            <p className="text-sm text-gray-600">{mission.pickup.address}</p>
            {mission.pickup.landmark && (
              <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                <MapPin className="w-3 h-3" />
                {mission.pickup.landmark}
              </p>
            )}
          </div>
          <div className="bg-red-50 rounded-lg p-3">
            <p className="text-xs text-red-700 font-medium">POINT DE LIVRAISON</p>
            <p className="font-medium text-gray-900">{mission.dropoff.zone}</p>
            <p className="text-sm text-gray-600">{mission.dropoff.address}</p>
            {mission.dropoff.landmark && (
              <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                <MapPin className="w-3 h-3" />
                {mission.dropoff.landmark}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-4 p-3 bg-gray-50 rounded-lg">
        <div>
          <p className="text-xs text-gray-500">Type</p>
          <p className="font-medium text-gray-900">{mission.deliveryType}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Poids</p>
          <p className="font-medium text-gray-900">{mission.packageWeight} kg</p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Paiement</p>
          <p className="font-medium text-gray-900">{mission.paymentMethod}</p>
        </div>
      </div>

      <p className="text-sm text-gray-600 mb-4">{mission.packageDescription}</p>

      {mission.client && (
        <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg mb-4">
          <div className="flex items-center gap-3">
            <Avatar name={clientName} size="md" />
            <div>
              <p className="font-medium text-gray-900">{clientName}</p>
              <p className="text-sm text-gray-500">{mission.client.phone}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <a
              href={`tel:${mission.client.phone}`}
              className="p-2 rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200"
            >
              <Phone className="w-4 h-4" />
            </a>
            <button
              type="button"
              className="p-2 rounded-full bg-emerald-100 text-emerald-600 hover:bg-emerald-200"
              onClick={() => setShowChat((v) => !v)}
              disabled={!canChat}
              title={canChat ? 'Messages' : 'Messages disponibles après acceptation'}
            >
              <MessageSquare className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {showChat && canChat && (
        <div className="mb-4">
          <OrderChatPanel orderId={mission.id} />
        </div>
      )}

      <div className="flex items-center justify-between pt-4 border-t border-gray-100">
        <div>
          <p className="text-sm text-gray-500">Montant à percevoir</p>
          <p className="text-2xl font-bold text-emerald-600">{formatCurrency(mission.price)}</p>
        </div>
        <div className="flex gap-2">
          {canAcceptReject && onAccept && onReject && (
            <>
              <Button
                variant="outline"
                onClick={onReject}
                leftIcon={<X className="w-4 h-4" />}
                disabled={actionLoading}
              >
                Refuser
              </Button>
              <Button
                variant="success"
                onClick={onAccept}
                leftIcon={<Check className="w-4 h-4" />}
                isLoading={actionLoading}
              >
                Accepter
              </Button>
            </>
          )}
          {isInProgress && onComplete && (
            <Button
              onClick={onComplete}
              leftIcon={<Check className="w-4 h-4" />}
              isLoading={actionLoading}
            >
              Marquer livrée
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}
